import React, { useCallback, useRef, useState } from 'react';
import DiscardChangesDialog, {
  joinSectionLabels
} from './DiscardChangesDialog';
import { useAutoTranslation } from 'src/hooks/useTranslate';

/**
 * Ask before an action would leave the sidebar's unsaved section edits behind.
 *
 * TGCLOUD-1839 made closing a tab ask. Three other ways of leaving the same
 * edits still dropped them without a word: selecting someone else on the
 * canvas, "Open pedigree" from a patient tab, and closing the drawer. Each one
 * now calls `confirmDiscard(proceed)` instead of acting directly:
 *
 *   - nothing unsaved: `proceed` runs at once, so a clean sidebar behaves
 *     exactly as it always has;
 *   - otherwise the shared DiscardChangesDialog opens, naming the sections and
 *     whose record they are on. "Keep editing" (the default, and what Escape
 *     and a backdrop click do) drops `proceed`. The destructive button runs
 *     `onDiscard` first, which is the save footer's own discard path, and only
 *     then `proceed`, so nothing stale is left behind to be saved later.
 *
 * `confirmDiscard` is stable and reads the latest labels through a ref, so it
 * can be handed to ChartBox once and called from event handlers that d3 bound
 * long ago.
 *
 * `confirmLabel` is the destructive button's ENGLISH wording ("Discard and
 * close"); the dialog translates it when it shows, so a caller passes it
 * untranslated.
 *
 * @param {object}   args
 * @param {string[]} args.dirtySectionLabels  what useDirtyState reports
 * @param {{kind: 'person'|'family', label: string}|null} args.subject
 *        whose record the sections belong to, as the pinned header names it
 *        (so an anonymous pedigree names the member id, never the person)
 * @param {Function} args.onDiscard  resets every dirty section
 * @returns {{ confirmDiscard: Function, discardDialog: React.ReactNode }}
 */
export default function useDiscardGuard({
  dirtySectionLabels,
  subject,
  onDiscard
}) {
  const t = useAutoTranslation();

  // { open, proceed, confirmLabel, sections, subject }. Kept, not cleared, when
  // the dialog closes, so its text does not change while it fades out.
  const [pending, setPending] = useState(null);
  const latestRef = useRef(null);
  latestRef.current = { dirtySectionLabels, subject, onDiscard };

  const confirmDiscard = useCallback(
    (proceed, { confirmLabel = 'Discard and switch' } = {}) => {
      const { dirtySectionLabels: sections, subject: who } = latestRef.current;
      if (!Array.isArray(sections) || sections.length === 0) {
        proceed();
        return true;
      }
      setPending({
        open: true,
        proceed,
        confirmLabel,
        sections: [...sections],
        subject: who || null
      });
      return false;
    },
    []
  );

  const close = () => setPending((p) => (p ? { ...p, open: false } : p));

  const handleConfirm = () => {
    if (!pending?.open) return;
    const { proceed } = pending;
    close();
    latestRef.current.onDiscard?.();
    proceed();
  };

  const discardDialog = (
    <DiscardChangesDialog
      open={Boolean(pending?.open)}
      onCancel={close}
      onConfirm={handleConfirm}
      cancelLabel={t('Keep editing')}
      confirmLabel={
        pending?.confirmLabel ? t(pending.confirmLabel) : t('Discard')
      }
      ariaLabel={t('Confirm leaving unsaved changes')}
    >
      {pending ? (
        <UnsavedSectionsSentence
          sections={pending.sections}
          subject={pending.subject}
        />
      ) : null}
    </DiscardChangesDialog>
  );

  return { confirmDiscard, discardDialog };
}

/**
 * "Unsaved changes to Person for Jane Doe will be lost." Phrasing content only,
 * because DiscardChangesDialog puts it in a paragraph.
 */
export function UnsavedSectionsSentence({ sections, subject }) {
  const t = useAutoTranslation();

  const what = <strong>{joinSectionLabels(sections, t)}</strong>;
  if (subject?.label && subject.kind === 'family') {
    return (
      <>
        {t('Unsaved changes to')} {what} {t('on family record')}{' '}
        <strong>{subject.label}</strong> {t('will be lost.')}
      </>
    );
  }
  if (subject?.label) {
    return (
      <>
        {t('Unsaved changes to')} {what} {t('for')}{' '}
        <strong>{subject.label}</strong> {t('will be lost.')}
      </>
    );
  }
  return (
    <>
      {t('Unsaved changes to')} {what} {t('will be lost.')}
    </>
  );
}
